"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { Mail, Phone, Calendar, TrendingUp } from "lucide-react";

const TOP_CUSTOMERS = [
  { initial: "M", name: "Maria González", email: "maria.g@example.com", phone: "(214) 555-0142", visits: 24, ltv: 487, last: "2 days ago", hue: 320 },
  { initial: "J", name: "James Carter", email: "jcarter@example.com", phone: "(214) 555-0207", visits: 18, ltv: 392, last: "1 week ago", hue: 260 },
  { initial: "A", name: "Alex Rivera", email: "alex.r@example.com", phone: "(214) 555-0388", visits: 31, ltv: 612, last: "Today", hue: 30, top: true },
  { initial: "D", name: "Daniela López", email: "dani@example.com", phone: "(214) 555-0476", visits: 14, ltv: 278, last: "3 days ago", hue: 290 },
];

export default function LoyaltyDatabaseScroll() {
  const { t } = useLang();

  return (
    <section className="relative py-32 px-6 bg-[#FAFAF8] overflow-hidden">
      <div
        className="absolute bottom-0 right-0 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(244,114,43,0.10) 0%, transparent 65%)" }}
      />

      <div className="relative max-w-[1200px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 max-w-[760px] mx-auto"
        >
          <div className="text-[11px] font-black tracking-widest uppercase mb-4" style={{ color: "#D7263D" }}>
            {t("Customer database", "Base de clientes")}
          </div>
          <h2 className="text-4xl md:text-[64px] font-black tracking-[-0.03em] leading-[1.02] text-redy-charcoal">
            {t("Every order ", "Cada orden ")}
            <span className="text-redy-charcoal/30">{t("adds a line.", "agrega una línea.")}</span>
          </h2>
          <p className="text-[16px] font-medium text-redy-secondary leading-relaxed mt-6">
            {t(
              "Email captured at checkout. Phone captured at SMS opt-in. Visits, total spent, last visit, average ticket — calculated on the fly. Searchable, in one place.",
              "Email capturado al pagar. Teléfono capturado en el opt-in de SMS. Visitas, gasto total, última visita, ticket promedio — calculado al vuelo. Buscable, en un solo lugar."
            )}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.21, 0.47, 0.32, 0.98] }}
          className="bg-white border border-black/5 rounded-[28px] p-6 md:p-8 max-w-[920px] mx-auto shadow-[0_20px_60px_rgba(0,0,0,0.04)]"
        >
          {/* Header */}
          <div className="flex items-baseline justify-between mb-6">
            <div>
              <div className="text-[10px] font-black tracking-widest uppercase text-redy-secondary/60 mb-1">
                {t("Top customers by LTV", "Top clientes por LTV")}
              </div>
              <div className="text-[16px] font-black text-redy-charcoal">
                1,247 {t("total", "totales")}
              </div>
            </div>
            <div className="flex items-baseline gap-2">
              <div
                className="text-[24px] font-black tabular-nums"
                style={{
                  background: "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  color: "transparent",
                }}
              >
                $87k
              </div>
              <div className="text-[11px] font-bold text-redy-secondary">
                {t("lifetime", "lifetime")}
              </div>
            </div>
          </div>

          {/* Customer rows */}
          <div className="space-y-2 mb-6">
            {TOP_CUSTOMERS.map((c, i) => (
              <motion.div
                key={c.email}
                initial={{ opacity: 0, x: -16 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.45, delay: 0.1 + 0.08 * i }}
                className={`rounded-[16px] p-4 border flex items-center gap-4 ${
                  c.top ? "" : "bg-[#FAFAF8] border-black/5"
                }`}
                style={
                  c.top
                    ? {
                        backgroundColor: "rgba(244,114,43,0.06)",
                        borderColor: "rgba(244,114,43,0.20)",
                      }
                    : undefined
                }
              >
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white font-black text-[13px] shrink-0"
                  style={{
                    background: c.top
                      ? "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)"
                      : `hsl(${c.hue}, 60%, 55%)`,
                  }}
                >
                  {c.initial}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="text-[13px] font-black text-redy-charcoal truncate">{c.name}</div>
                    {c.top && (
                      <div
                        className="text-[8px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-full text-white"
                        style={{ backgroundColor: "#F4722B" }}
                      >
                        {t("Top fan", "Top fan")}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-[10px] text-redy-secondary mt-0.5">
                    <span className="flex items-center gap-1 min-w-0">
                      <Mail className="w-2.5 h-2.5 shrink-0" />
                      <span className="truncate">{c.email}</span>
                    </span>
                    <span className="hidden sm:flex items-center gap-1 shrink-0">
                      <Phone className="w-2.5 h-2.5" />
                      <span>{c.phone}</span>
                    </span>
                  </div>
                </div>

                {/* Metrics */}
                <div className="text-right shrink-0">
                  <div className="text-[14px] font-black text-redy-charcoal tabular-nums">${c.ltv}</div>
                  <div className="text-[9px] font-bold uppercase tracking-wider text-redy-secondary/60">
                    {c.visits} {t("visits", "visitas")}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Footer stats */}
          <div className="grid grid-cols-3 gap-3 pt-5 border-t border-black/5">
            <div className="text-center">
              <Calendar className="w-3.5 h-3.5 mx-auto mb-1.5" style={{ color: "#F4722B" }} />
              <div className="text-[18px] font-black text-redy-charcoal tabular-nums">3.2</div>
              <div className="text-[9px] font-bold uppercase tracking-wider text-redy-secondary/60">
                {t("Avg visits", "Visitas prom")}
              </div>
            </div>
            <div className="text-center">
              <TrendingUp className="w-3.5 h-3.5 mx-auto mb-1.5" style={{ color: "#F4722B" }} />
              <div className="text-[18px] font-black text-redy-charcoal tabular-nums">68%</div>
              <div className="text-[9px] font-bold uppercase tracking-wider text-redy-secondary/60">
                {t("Repeat rate", "Repetición")}
              </div>
            </div>
            <div className="text-center">
              <div className="w-3.5 h-3.5 mx-auto mb-1.5 rounded-full" style={{ backgroundColor: "#F4722B" }} />
              <div className="text-[18px] font-black text-redy-charcoal tabular-nums">$70</div>
              <div className="text-[9px] font-bold uppercase tracking-wider text-redy-secondary/60">
                {t("Avg LTV", "LTV prom")}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
